import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { RollMaterial } from "../models/RollMaterial";
import {
	removeRollMaterial,
	upsertRollMaterial,
} from "../repositories/RollMaterialsRepository";
import { ROLL_MATERIALS_QUERY_KEYS } from "./useRollMaterials";

interface MutationOptions {
	onSuccess?: (data?: RollMaterial) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRollMaterial = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (material: RollMaterial) => {
			return await upsertRollMaterial(material);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ROLL_MATERIALS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRollMaterial: mutation.mutate,
		upsertRollMaterialAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteRollMaterial = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeRollMaterial(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ROLL_MATERIALS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteRollMaterial: mutation.mutate,
		deleteRollMaterialAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
